/**
 * Tartı Yönetimi — cihaz envanteri
 *
 * Tartı tanımları SQL'de (`Scales` tablosu) tutulur çünkü aynı cihaza hem kasa
 * (canlı tartım) hem stok ekranı (PLU gönderimi) erişiyor. Kasa hangi tartıyı
 * kullanacağını Hızlı Satış ayarlarından bu listeden seçer.
 *
 * Bağlantı testi local-service üzerinden yapılır — cihaza ulaşabilen tek yer
 * orası. Test, bu ekranı açan bilgisayarın yerel servisini kullanır.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import RefreshIcon from '@mui/icons-material/Refresh';

import { useAuth } from '../../contexts/AuthContext';
import scaleApi, { ScaleDto, SaveScalePayload } from '../../services/scaleService';
import {
  DEFAULT_SCALE_PORT,
  ScaleInfo,
  ScaleReading,
  testScaleConnection,
} from '../../services/localScaleService';
import GuideButton from '../common/guide/GuideButton';

// Bugün tek sürücü var. Yeni marka eklenince buraya bir satır daha girer;
// etiket yazıcılarındaki dil seçimiyle (PPLA/PPLB/ZPL) aynı kalıp.
const SCALE_MODELS = [
  { value: 'CAS_CL', label: 'CAS CL Serisi (CL3000, CL5000)' },
];

const EMPTY_FORM: SaveScalePayload = {
  name: '',
  description: '',
  host: '',
  port: DEFAULT_SCALE_PORT,
  model: 'CAS_CL',
  barcodePrefix: '27',
  branchId: '',
  branchName: '',
  isActive: true,
};

interface TestResult {
  info: ScaleInfo;
  weight: ScaleReading;
}

/** gts_sales_api hata gövdesini PascalCase döndürüyor (`{ Success, Message }`). */
function errorMessage(err: any, fallback: string): string {
  const body = err?.response?.data;
  return body?.Message || body?.message || err?.message || fallback;
}

const ScaleManagement: React.FC = () => {
  const { branches: authBranches, loadBranches } = useAuth();
  const isMobile = useMediaQuery('(max-width: 1099px)');

  const [scales, setScales] = useState<ScaleDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<SaveScalePayload>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<ScaleDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  const branches = authBranches.filter((b) => b.id !== 'null');

  const loadScales = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setScales(await scaleApi.getAll());
    } catch (err: any) {
      setError(errorMessage(err, 'Tartılar getirilemedi.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadScales();
    if (branches.length === 0) loadBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setTestResult(null);
    setTestError(null);
    setFormOpen(true);
  };

  const openEdit = (scale: ScaleDto) => {
    setEditingId(scale.id);
    setForm({
      id: scale.id,
      name: scale.name,
      description: scale.description || '',
      host: scale.host,
      port: scale.port,
      model: scale.model || 'CAS_CL',
      barcodePrefix: scale.barcodePrefix || '27',
      branchId: scale.branchId || '',
      branchName: scale.branchName || '',
      isActive: scale.isActive,
    });
    setFormError(null);
    setTestResult(null);
    setTestError(null);
    setFormOpen(true);
  };

  const handleBranchChange = (branchId: string) => {
    const branch = branches.find((b) => b.id === branchId);
    setForm((prev) => ({ ...prev, branchId, branchName: branch?.name || '' }));
  };

  /** Testi kaydetmeden de yapabilmek önemli: yanlış IP'yi kaydetmeden fark edin. */
  const runTest = async (host: string, port: number, model: string) => {
    setTesting(true);
    setTestResult(null);
    setTestError(null);
    try {
      setTestResult(await testScaleConnection({ host, port, model }));
    } catch (err: any) {
      setTestError(err?.message || 'Tartıya bağlanılamadı.');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setFormError('Tartı adı zorunludur.');
      return;
    }
    if (!form.host.trim()) {
      setFormError('IP adresi zorunludur.');
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      await scaleApi.save({
        ...form,
        id: editingId ?? undefined,
        name: form.name.trim(),
        host: form.host.trim(),
        port: Number(form.port) || DEFAULT_SCALE_PORT,
        barcodePrefix: (form.barcodePrefix || '27').trim(),
      });
      setFormOpen(false);
      setSnackbar(editingId ? 'Tartı güncellendi.' : 'Tartı eklendi.');
      loadScales();
    } catch (err: any) {
      setFormError(errorMessage(err, 'Tartı kaydedilemedi.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await scaleApi.remove(deleteTarget.id);
      setDeleteTarget(null);
      setSnackbar('Tartı silindi.');
      loadScales();
    } catch (err: any) {
      setError(errorMessage(err, 'Tartı silinemedi.'));
    } finally {
      setDeleting(false);
    }
  };

  const renderActions = (scale: ScaleDto) => (
    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
      <Tooltip title="Bağlantıyı test et">
        <IconButton
          size="small"
          onClick={() => {
            openEdit(scale);
            runTest(scale.host, scale.port, scale.model);
          }}
        >
          <NetworkCheckIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Düzenle">
        <IconButton size="small" onClick={() => openEdit(scale)}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Sil">
        <IconButton size="small" color="error" onClick={() => setDeleteTarget(scale)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexGrow: 1 }}>
          <MonitorWeightIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Tartılar
              </Typography>
              <GuideButton guideKey="devices.scales" size="small" />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Ağa bağlı tartıları tanımlayın. Kasa, kullanacağı tartıyı Hızlı Satış
              ayarlarından bu listeden seçer.
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={loadScales} disabled={loading}>
            Yenile
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Yeni Tartı
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : scales.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <MonitorWeightIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            Henüz tartı tanımlanmamış
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Tartının ağ ayarlarından IP adresini öğrenip buraya ekleyin.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Yeni Tartı
          </Button>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {scales.map((scale) => (
            <Paper key={scale.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600 }}>{scale.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {scale.host}:{scale.port}
                  </Typography>
                  {scale.branchName && (
                    <Typography variant="caption" color="text.secondary">
                      {scale.branchName}
                    </Typography>
                  )}
                </Box>
                <Chip
                  size="small"
                  label={scale.isActive ? 'Aktif' : 'Pasif'}
                  color={scale.isActive ? 'success' : 'default'}
                />
              </Stack>
              <Divider sx={{ my: 1 }} />
              {renderActions(scale)}
            </Paper>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'common.white', fontWeight: 600 }}>Ad</TableCell>
                <TableCell sx={{ color: 'common.white', fontWeight: 600 }}>Adres</TableCell>
                <TableCell sx={{ color: 'common.white', fontWeight: 600 }}>Model</TableCell>
                <TableCell sx={{ color: 'common.white', fontWeight: 600 }}>Şube</TableCell>
                <TableCell sx={{ color: 'common.white', fontWeight: 600 }}>Barkod Ön Eki</TableCell>
                <TableCell sx={{ color: 'common.white', fontWeight: 600 }}>Durum</TableCell>
                <TableCell align="right" sx={{ color: 'common.white', fontWeight: 600 }}>
                  İşlemler
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scales.map((scale) => (
                <TableRow key={scale.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {scale.name}
                    </Typography>
                    {scale.description && (
                      <Typography variant="caption" color="text.secondary">
                        {scale.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {scale.host}:{scale.port}
                  </TableCell>
                  <TableCell>
                    {SCALE_MODELS.find((m) => m.value === scale.model)?.label || scale.model}
                  </TableCell>
                  <TableCell>{scale.branchName || '—'}</TableCell>
                  <TableCell>{scale.barcodePrefix || '27'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={scale.isActive ? 'Aktif' : 'Pasif'}
                      color={scale.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">{renderActions(scale)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Ekleme / düzenleme */}
      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <MonitorWeightIcon />
            <span>{editingId ? 'Tartıyı Düzenle' : 'Yeni Tartı'}</span>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Tartı adı"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Manav Tartısı"
              fullWidth
              required
              autoFocus
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="IP adresi"
                value={form.host}
                onChange={(e) => setForm((p) => ({ ...p, host: e.target.value }))}
                placeholder="192.168.1.243"
                fullWidth
                required
                helperText="Tartının ağ ayarlarında yazan adres"
              />
              <TextField
                label="Port"
                type="number"
                value={form.port}
                onChange={(e) => setForm((p) => ({ ...p, port: Number(e.target.value) }))}
                sx={{ width: { sm: 160 } }}
                helperText="CAS: 20304"
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label="Model"
                value={form.model}
                onChange={(e) => setForm((p) => ({ ...p, model: e.target.value }))}
                fullWidth
              >
                {SCALE_MODELS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Barkod ön eki"
                value={form.barcodePrefix || ''}
                onChange={(e) => setForm((p) => ({ ...p, barcodePrefix: e.target.value }))}
                sx={{ width: { sm: 160 } }}
                helperText="Varsayılan 27"
              />
            </Stack>

            <TextField
              select
              label="Şube"
              value={form.branchId || ''}
              onChange={(e) => handleBranchChange(e.target.value)}
              fullWidth
            >
              <MenuItem value="">Şube seçilmedi</MenuItem>
              {branches.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Açıklama"
              value={form.description || ''}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                />
              }
              label="Aktif"
            />

            {formError && <Alert severity="error">{formError}</Alert>}
            {testError && <Alert severity="error">{testError}</Alert>}
            {testResult && (
              <Alert severity="success">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Tartıya bağlanıldı
                </Typography>
                <Typography variant="body2">
                  Kapasite: {testResult.info.capacityKg ?? '?'} kg · Birim: {testResult.info.unit} ·{' '}
                  Hassasiyet: {testResult.info.weightDecimals} ondalık
                </Typography>
                <Typography variant="body2">
                  Anlık ağırlık: <strong>{testResult.weight.kg} kg</strong>
                </Typography>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            startIcon={<NetworkCheckIcon />}
            onClick={() => runTest(form.host.trim(), Number(form.port) || DEFAULT_SCALE_PORT, form.model)}
            disabled={testing || !form.host.trim()}
          >
            {testing ? 'Deneniyor...' : 'Bağlantıyı Test Et'}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setFormOpen(false)} color="inherit" disabled={saving}>
            Vazgeç
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Silme onayı */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Tartıyı sil</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{deleteTarget?.name}</strong> silinecek. Bu tartıyı kullanan kasaların
            tartı seçimi de temizlenir.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} color="inherit" disabled={deleting}>
            Vazgeç
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Siliniyor...' : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default ScaleManagement;
